import { useDeferredValue, useMemo, useState } from "react";
import {
  DEFAULT_HORIZON_PAD,
  MAX_HORIZON_AGE,
  projectRetirement,
  solveEarliestRetireAge,
  solveRequiredMonthlyContribution,
} from "@/lib/retirement-calc";
import RetirementInputs, {
  type RetirementFormState,
} from "@/components/tools/retirement/Inputs";
import ProjectionChart from "@/components/tools/retirement/ProjectionChart";
import Summary from "@/components/tools/retirement/Summary";
import InsightCard from "@/components/tools/retirement/InsightCard";

const CURRENCY = "EUR";

const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));

interface Props {
  /** Pre-set retirement age (50, 55, 60, …). */
  targetAge: number;
  /** Sensible default for current age. */
  defaultCurrentAge?: number;
}

const EmbeddedRetirementCalculator = ({
  targetAge,
  defaultCurrentAge = Math.max(22, targetAge - 20),
}: Props) => {
  const initial: RetirementFormState = useMemo(
    () => ({
      currentAge: defaultCurrentAge,
      targetAge,
      currentSavings: 10_000,
      monthlyContribution: 500,
      monthlyExpenses: 2_500,
      annualReturnPct: 8,
      inflationPct: 2,
      shariahMode: false,
      countryCode: "",
      customTaxRatePct: 0,
      showAfterTax: true,
    }),
    [defaultCurrentAge, targetAge],
  );

  const [state, setState] = useState<RetirementFormState>(initial);

  const setTargetAge = (age: number) =>
    setState((p) => ({ ...p, targetAge: clamp(age, p.currentAge + 1, 80) }));
  const setMonthlyExpenses = (v: number) =>
    setState((p) => ({ ...p, monthlyExpenses: clamp(v, 200, 50_000_000) }));
  const setNestEggTarget = (target: number) => {
    const monthly = Math.max(200, Math.round(target / 25 / 12));
    setMonthlyExpenses(monthly);
  };

  const chartHorizon = useMemo(
    () => Math.min(MAX_HORIZON_AGE, state.targetAge + DEFAULT_HORIZON_PAD),
    [state.targetAge],
  );

  const result = useMemo(
    () => projectRetirement(state, chartHorizon),
    [state, chartHorizon],
  );

  const deferred = useDeferredValue(state);
  const requiredMonthly = useMemo(
    () => solveRequiredMonthlyContribution(deferred),
    [deferred],
  );
  const earliestAge = useMemo(
    () => solveEarliestRetireAge(deferred),
    [deferred],
  );

  const fmtFull = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: CURRENCY,
        maximumFractionDigits: 0,
      }),
    [],
  );
  const fmtCompact = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: CURRENCY,
        maximumFractionDigits: 1,
        notation: "compact",
      }),
    [],
  );

  return (
    <section
      aria-label={`Retirement calculator pre-set to age ${targetAge}`}
      className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5 lg:gap-8"
    >
      <div className="order-2 lg:order-1">
        <div className="rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-sm">
          <RetirementInputs
            state={state}
            onChange={setState}
            onReset={() => setState(initial)}
          />
        </div>
      </div>

      <div className="order-1 lg:order-2 space-y-5 min-w-0">
        <InsightCard
          result={result}
          targetAge={state.targetAge}
          currentAge={state.currentAge}
          monthlyExpenses={state.monthlyExpenses}
          monthlyContribution={state.monthlyContribution}
          requiredMonthly={requiredMonthly}
          earliestAge={earliestAge}
          fmtCompact={fmtCompact}
          fmtFull={fmtFull}
          onTargetAgeChange={setTargetAge}
          onMonthlyExpensesChange={setMonthlyExpenses}
          onNestEggTargetChange={setNestEggTarget}
        />

        <Summary
          result={result}
          fmtCompact={fmtCompact}
          fmtFull={fmtFull}
          inflationPct={state.inflationPct}
          shariahMode={state.shariahMode}
        />

        <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-sm">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-lg font-bold">Asset growth vs target</h2>
            <span className="text-xs text-muted-foreground">Today's €</span>
          </div>
          <ProjectionChart
            rows={result.rows}
            currency={CURRENCY}
            locale="en-US"
            shariahMode={state.shariahMode}
            targetAge={state.targetAge}
          />
        </div>
      </div>
    </section>
  );
};

export default EmbeddedRetirementCalculator;
